// What? The main app component.
// Why? To define the main routes and components of the app.
// How?
// - Imports the necessary libraries.
// - Sets up the routes and components.
// - Renders the main components.

import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Import views for core routes
import UserDashboard from "./views/UserDashboard";
import OrganizationBranding from "./views/OrganizationBranding";
import AgentSkillStore from "./views/AgentSkillStore";
import OrgHeartbeatsOverview from "./views/OrgHeartbeatsOverview";
import AdminIntegrations from "./views/AdminIntegrations";
import MetalMindAdminLayout from "./views/MetalMindAdminLayout";
import MetalMindAdminFeatureFlags from "./views/MetalMindAdminFeatureFlags";
import MetalMindAdminUsers from "./views/MetalMindAdminUsers";
import ChangePlan from "./views/ChangePlan";
import SelectPackage from "./views/SelectPackage";
import DemoPage from "./views/DemoPage";
import PageLoading from "./components/PageLoading";
import MaintenanceGate from "./components/MaintenanceGate";
import ServiceNoticeBanner from "./components/ServiceNoticeBanner";
import OrgSettingsOverview from "./views/OrgSettingsOverview";
import OrgSettingsMembers from "./views/OrgSettingsMembers";
import OrgSettingsDomains from "./views/OrgSettingsDomains";
import OrgSettingsBilling from "./views/OrgSettingsBilling";
import OrgSettingsEmail from "./views/OrgSettingsEmail";
import OrgSettingsAdvanced from "./views/OrgSettingsAdvanced";
import { DashboardLayout } from "./modules/dashboards/DashboardLayout";
import { ModuleDashboardsOverview } from "./modules/dashboards/views/ModuleDashboardsOverview";
import { ModuleDashboardDetail } from "./modules/dashboards/views/ModuleDashboardDetail";

// Nav container
import NavContainer from "./components/NavContainer";
import NavContainerNoSidebar from "./components/NavContainerNoSidebar";
import NavContainerOrgSettings from "./components/NavContainerOrgSettings";
import AuthProvider from "./components/AuthProvider";
import AuthenticatedErrorReporting from "./components/AuthenticatedErrorReporting";
import ErrorMessageProvider from "./components/ErrorMessageProvider";
import { WorkspaceProvider } from "./providers/WorkspaceProvider";
import { FeatureFlagProvider } from "./providers/FeatureFlagProvider";
import { OrgThemeProvider } from "./providers/OrgThemeProvider";
import OrgSettingsProvider from "./providers/OrgSettingsProvider";
import { TutorialProvider } from "./components/Tutorial";
import { getRuntimeConfig } from "./config/runtimeConfig";
import { consumeAuthReturnTarget } from "./utils/authReturnTarget";

// Dynamic workspace routes
import AppRoutes from "./AppRoutes";
import { CssVarsProvider } from "@mui/joy/styles";
import { CssBaseline } from "@mui/joy";
import { theme } from "./theme/themes";

// Lazy-loaded public document viewer (no auth required)
const PublicDocumentViewer = React.lazy(
  () => import("./modules/ida/views/PublicDocumentViewer"),
);
const PublicCanvasViewer = React.lazy(
  () => import("./modules/myvault/views/PublicCanvasViewer"),
);
// Landing page for the invitation link in the invite email. Public by design:
// the token is the credential, and the invitee may have no way to sign in yet.
const InvitationAccept = React.lazy(() => import("./views/InvitationAccept"));

// Check payment method to conditionally show subscription route
const paymentMethod = getRuntimeConfig().paymentMethod;
const showSubscriptionRoute = paymentMethod !== "ENTERPRISE";

// Create a React Query client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30, // 30 seconds
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Layout component for home routes (no sidebar)
const HomeLayout: React.FC = () => (
  <NavContainerNoSidebar>
    <Outlet />
  </NavContainerNoSidebar>
);

// Layout component for workspace routes (with sidebar)
const WorkspaceLayout: React.FC = () => (
  <WorkspaceProvider>
    <FeatureFlagProvider>
      <NavContainer>
        <AppRoutes />
      </NavContainer>
    </FeatureFlagProvider>
  </WorkspaceProvider>
);

// Layout component for org settings routes (with org settings sidebar)
const OrgSettingsLayout: React.FC = () => (
  <OrgSettingsProvider>
    <NavContainerOrgSettings>
      <Outlet />
    </NavContainerOrgSettings>
  </OrgSettingsProvider>
);

/** Redirect helper for published document auth flow.
 * After authentication completes, reads the `to` query param and navigates
 * there. This lets PublicDocumentViewer bounce unauthenticated users through
 * the login flow and back to the original document URL. */
const AuthReturnRedirect: React.FC = () => {
  React.useEffect(() => {
    // Navigate via full page load so PublicDocumentViewer re-mounts
    // with the now-cached auth token in localStorage
    window.location.replace(consumeAuthReturnTarget(window.location.search));
  }, []);
  return <PageLoading />;
};

const ApplicationRoutes: React.FC = () => (
  <AuthProvider>
    <AuthenticatedErrorReporting>
      {/* Inside AuthProvider so the overlay can call authFetch when a hint is
        dismissed, and above OrgThemeProvider so the nested-CssVarsProvider
        swap there cannot remount it mid-session. */}
      <TutorialProvider>
        <OrgThemeProvider>
          <MaintenanceGate>
            <Routes>
              {/* Auth return redirect -- bounce back to published doc URL after login */}
              <Route path="/auth/return" element={<AuthReturnRedirect />} />
              {/* Published MyVault links require the same sign-in as workspace routes. */}
              <Route
                path="/public/canvases/:canvasId"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <PublicCanvasViewer />
                  </Suspense>
                }
              />

              {/* Home routes wrapped in NavContainerNoSidebar */}
              <Route element={<HomeLayout />}>
                <Route path="/" element={<UserDashboard />} />
                {showSubscriptionRoute && (
                  <>
                    {/* Redirect old subscription route to select package page */}
                    <Route
                      path="/subscription"
                      element={
                        <Navigate to="/subscription/select-package" replace />
                      }
                    />
                    <Route
                      path="/subscription/select-package"
                      element={<SelectPackage />}
                    />
                    <Route
                      path="/subscription/change-plan"
                      element={<ChangePlan />}
                    />
                  </>
                )}
                <Route
                  path="/admin/metalmind"
                  element={<MetalMindAdminLayout />}
                >
                  <Route
                    index
                    element={<Navigate to="feature-flags" replace />}
                  />
                  <Route
                    path="feature-flags"
                    element={<MetalMindAdminFeatureFlags />}
                  />
                  <Route path="users" element={<MetalMindAdminUsers />} />
                </Route>
                <Route path="/demopage" element={<DemoPage />} />

                {/* Redirects for old org settings URLs */}
                <Route
                  path="/organization/branding"
                  element={
                    <Navigate to="/organization/settings/branding" replace />
                  }
                />
                <Route
                  path="/admin/integrations"
                  element={
                    <Navigate
                      to="/organization/settings/integrations"
                      replace
                    />
                  }
                />
                <Route
                  path="/admin/org/heartbeats-overview"
                  element={
                    <Navigate to="/organization/settings/heartbeats" replace />
                  }
                />
              </Route>

              {/* Org settings routes wrapped in OrgSettingsLayout (with org settings sidebar) */}
              <Route element={<OrgSettingsLayout />}>
                <Route
                  path="/organization/settings/dashboards"
                  element={<DashboardLayout />}
                >
                  <Route index element={<ModuleDashboardsOverview />} />
                  <Route
                    path=":module_id"
                    element={<ModuleDashboardDetail />}
                  />
                </Route>
                <Route
                  path="/organization/settings"
                  element={<OrgSettingsOverview />}
                />
                <Route
                  path="/organization/settings/members"
                  element={<OrgSettingsMembers />}
                />
                <Route
                  path="/organization/settings/domains"
                  element={<OrgSettingsDomains />}
                />
                <Route
                  path="/organization/settings/billing"
                  element={<OrgSettingsBilling />}
                />
                <Route
                  path="/organization/settings/branding"
                  element={<OrganizationBranding />}
                />
                <Route
                  path="/organization/settings/email"
                  element={<OrgSettingsEmail />}
                />
                <Route
                  path="/organization/settings/agent-skills"
                  element={<AgentSkillStore />}
                />
                <Route
                  path="/organization/settings/agent-skills/:offering_key"
                  element={<AgentSkillStore />}
                />
                <Route
                  path="/organization/settings/integrations"
                  element={<AdminIntegrations />}
                />
                <Route
                  path="/organization/settings/heartbeats"
                  element={<OrgHeartbeatsOverview />}
                />
                <Route
                  path="/organization/settings/advanced"
                  element={<OrgSettingsAdvanced />}
                />
                {/* Legacy redirect */}
                <Route
                  path="/organization/settings/ida/skills"
                  element={
                    <Navigate
                      to="/organization/settings/agent-skills"
                      replace
                    />
                  }
                />
              </Route>

              {/* Workspace routes wrapped in NavContainer */}
              <Route
                path="/workspace/:workspace_id/*"
                element={<WorkspaceLayout />}
              />

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </MaintenanceGate>
        </OrgThemeProvider>
      </TutorialProvider>
    </AuthenticatedErrorReporting>
  </AuthProvider>
);

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <CssVarsProvider theme={theme} defaultMode="light">
        <CssBaseline />
        <ErrorMessageProvider>
          <ServiceNoticeBanner />
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            {/* Maintenance mode is enforced post-authentication inside
                ApplicationRoutes (see MaintenanceGate), so the login flow and
                the public viewers below stay reachable while it is active. */}
            <Routes>
              {/* Public document viewer - no authentication required.
                  Uses wildcard (*) because document IDs contain slashes
                  (e.g. workspace_id/doc_hash) which get decoded from %2F
                  when links are shared through Slack, email, etc. */}
              <Route
                path="/public/documents/*"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <PublicDocumentViewer />
                  </Suspense>
                }
              />
              {/* Invitation acceptance - no authentication required. The
                  backend mounts /api/invitations/{details,accept} without auth
                  for the same reason, so an invitee with no account yet can
                  still join. Must stay outside ApplicationRoutes: that subtree
                  starts with AuthProvider, which would bounce them to login. */}
              <Route
                path="/invitations/accept"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <InvitationAccept />
                  </Suspense>
                }
              />
              {/* All other routes go through authentication */}
              <Route path="/*" element={<ApplicationRoutes />} />
            </Routes>
          </Router>
        </ErrorMessageProvider>
      </CssVarsProvider>
    </QueryClientProvider>
  );
};

export default App;
